import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";
import store from "@/store";
import RequestHandler from "@/assets/ts/requestHandler";

const config = window.$config;

const RH = new RequestHandler();

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: "/",
    name: "Home",
    component: () => import("@/views/LandingPage.vue"),
    meta: {
      name: "Home"
    }
  },
  {
    path: "/login",
    name: "Login",
    component: require("../views/Login.vue").default,
    meta: {
      name: "Login"
    }
  },
  {
    path: "/signup",
    name: "Signup",
    component: () => import("@/views/Signup.vue"),
    meta: {
      name: "Signup"
    }
  },
  {
    path: "/forgotUserPass",
    name: "ForgotUserPass",
    component: () => import("@/views/ForgotUserPass.vue"),
    meta: {
      name: "ForgotUserPass"
    }
  },
  {
    path: "/faqs",
    name: "FAQs",
    component: () => import("@/views/FAQs.vue"),
    meta: {
      name: "FAQs"
    }
  },
  {
    path: "/privacyPolicy",
    name: "PrivacyPolicy",
    component: () => import("@/views/PrivacyPolicy.vue"),
    meta: {
      name: "PrivacyPolicy"
    }
  },
  {
    path: "/passwordReset/:guid",
    name: "PasswordReset",
    component: () => import("@/views/PasswordReset.vue"),
    meta: {
      name: "PasswordReset"
    }
  },
  {
    path: "/administration",
    name: "Administration",
    component: () => import("@/views/Administration.vue"),
    meta: {
      name: "Administration",
      auth: {
        requiresLogin: true,
        permission: ["Administrator"]
      }
    }
  },
  {
    path: "/editUserAccount",
    name: "EditUserAccount",
    component: () => import("@/views/EditUserAccount.vue"),
    meta: {
      name: "EditUserAccount",
      auth: {
        requiresLogin: true,
        permission: ["Employer"]
      }
    }
  },
  {
    path: "/editMode",
    name: "EditMode",
    component: () => import("@/views/EditMode.vue"),
    meta: {
      name: "EditMode",
      auth: {
        requiresLogin: true,
        permission: ["EmployerEdit"]
      }
    }
  },
  {
    path: "/search",
    name: "Search",
    component: () => import("@/views/Search.vue"),
    meta: {
      name: "Search",
      auth: {
        requiresLogin: true,
        permission: ["Employer", "Employee"]
      }
    }
  },
  {
    path: "/searchResults",
    name: "SearchResults",
    component: () => import("@/views/SearchResults.vue"),
    meta: {
      name: "SearchResults",
      auth: {
        requiresLogin: true,
        permission: ["Employer", "Employee"]
      }
    }
  },
  {
    path: "/noResults/:vehicle",
    name: "NoResults",
    component: () => import("@/views/NoResults.vue"),
    meta: {
      name: "NoResults",
      auth: {
        requiresLogin: true,
        permission: ["Employer", "Employee"]
      }
    }
  },
  {
    path: "/employeeManagement",
    name: "EmployeeManagement",
    component: () => import("@/views/EmployeeManagement.vue"),
    meta: {
      name: "EmployeeManagement",
      auth: {
        requiresLogin: true,
        permission: ["Employer"]
      }
    }
  },
  {
    path: "/ncicHits",
    name: "NCICHits",
    component: () => import("@/views/NCICHits.vue"),
    meta: {
      name: "NCICHits",
      auth: {
        requiresLogin: true,
        permission: ["Administrator"]
      }
    }
  },
  {
    path: "/reports",
    name: "Reports",
    component: () => import("@/views/Reports.vue"),
    meta: {
      name: "Reports",
      auth: {
        requiresLogin: true,
        permission: ["Employer"]
      }
    }
  },
  {
    path: "/salvage",
    name: "Salvage",
    component: require("../views/Salvage.vue").default,
    meta: {
      name: "Salvage"
    }
  },
  {
    path: "/salvage/application",
    name: "SalvageApplication",
    component: () => import("@/views/SalvageApplication.vue"),
    meta: {
      name: "SalvageApplication"
    }
  },
  {
    path: "/salvage/application/status",
    name: "SalvageApplicationStatus",
    component: () => import("@/views/SalvageApplicationStatus.vue"),
    meta: {
      name: "SalvageApplicationStatus"
    }
  }
];

if (!config?.showLandingPage) {
  // Remove Home and salvage-related routes
  const routesToRemove = [
    "Home",
    "Salvage",
    "SalvageApplication",
    "SalvageApplicationStatus"
  ];
  routesToRemove.forEach(routeName => {
    const routeIndex = routes.findIndex(route => route.name === routeName);
    if (routeIndex !== -1) {
      routes.splice(routeIndex, 1);
    }
  });

  // Set Login page to root path
  const loginRoute = routes.find(route => route.name === "Login");
  if (loginRoute) {
    loginRoute.path = "/";
  }
}

const router = new VueRouter({
  routes
});

router.beforeEach(async (to, from, next) => {
  const sso = localStorage.getItem("ssoData");

  const userInfo = sso ?? localStorage.getItem("userInfo");

  if (userInfo != undefined) {
    store.dispatch("setUserInfo", JSON.parse(userInfo)).catch(err => {
      console.error(err);
    });
  }

  if (to.name === "Login" && sso) {
    if (store.getters.userInfo.accountType === "Administrator") {
      next({ name: "Administration", replace: true });
    } else if (store.getters.userInfo.accountType === "EmployerEdit") {
      next({ name: "EditMode", replace: true });
    } else {
      next({ name: "Search", replace: true });
    }
  }

  if (to.name != null) {
    if (
      to.meta!.auth &&
      to.meta!.auth.requiresLogin &&
      !store.getters.userInfo.isLoggedIn
    ) {
      next({
        name: "Login"
      });
    } else if (!to.meta!.auth) {
      if (store.getters.userInfo.isLoggedIn && !sso) {
        RH.logout(store.getters.userInfo.token);
      }
      next();
    } else {
      if (
        to.meta!.auth.permission.includes(store.getters.userInfo.accountType)
      ) {
        next();
      }
    }
  }
});

export default router;
